'use client';

import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { PrismicNextLink } from '@prismicio/next';
import { format } from 'date-fns';
import { Swiper as ISwiper } from 'swiper/types';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import classNames from 'classnames';

import { useUiStore } from '@/data';
import {
  useActiveHeaderSlice,
  useDispensaryTimeZone,
  useIsServerSide,
  useSelectedStore,
} from '@/hooks';
import { resolveLink, shouldHideNavigation, toZonedDate } from '@/utils';
import { useData } from '../DataProvider/DataProvider';
import { Alert } from '../molecules/alert';

import {
  filterPromotionRowsBySchedule,
  resolvePromotionBannerMessages,
} from './promotionBanner';
import style from './promotion.module.scss';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/autoplay';

interface IPromotionProps {
  className?: string;
  location?: 'layout' | 'header';
}

const Promotion: FC<IPromotionProps> = ({
  location = 'layout',
  className,
  ...props
}) => {
  const pathname = usePathname();
  const { promotion } = useData();
  const { isServerSide } = useIsServerSide();
  const { store } = useSelectedStore();
  const [state, setState] = useUiStore();
  const [scrollY, setScrollY] = useState(0);
  /** Expanded height for scroll threshold; null until first layout measure. */
  const [measuredPromotionHeight, setMeasuredPromotionHeight] = useState<
    number | null
  >(null);
  const [current, setCurrent] = useState({ dismissible: false, index: 0 });
  const [hiddenBaseIndices, setHiddenBaseIndices] = useState<Set<number>>(
    () => new Set(),
  );

  const activeSlice = useActiveHeaderSlice();
  const promotionRef = useRef<HTMLDivElement>(null);

  const timeZone = useDispensaryTimeZone();
  const currentDay = format(toZonedDate({ timeZone }), 'eeee');

  const scheduleRows = useMemo(
    () =>
      filterPromotionRowsBySchedule(
        promotion?.data?.location,
        currentDay,
        timeZone,
      ),
    [promotion?.data?.location, currentDay, timeZone],
  );

  const { messages } = useMemo(
    () =>
      resolvePromotionBannerMessages(promotion, scheduleRows, store?.url_slug),
    [promotion, scheduleRows, store?.url_slug],
  );

  const delay = promotion?.data?.delay;

  const baseActives = useMemo(() => messages.filter(m => m.active), [messages]);

  useEffect(() => {
    setHiddenBaseIndices(new Set());
  }, [messages]);

  const actives = useMemo(
    () => baseActives.filter((_, i) => !hiddenBaseIndices.has(i)),
    [baseActives, hiddenBaseIndices],
  );

  const activesRef = useRef(actives);
  activesRef.current = actives;

  useEffect(() => {
    if (!actives.length) return;
    setCurrent(prev => {
      const nextIndex = Math.min(prev.index, actives.length - 1);
      return {
        dismissible: actives[nextIndex]?.dismissible ?? false,
        index: nextIndex,
      };
    });
  }, [actives]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const cleanPath = pathname?.replace(/\/$/, '');
  const isHome = pathname === '/' || cleanPath === '/home';
  const isFloatingHeader = !!activeSlice?.primary?.floating;
  const isPromotionCollapsedOnScroll =
    location === 'layout' &&
    measuredPromotionHeight != null &&
    scrollY > measuredPromotionHeight &&
    isFloatingHeader &&
    isHome;

  const isPromotionCollapsedRef = useRef(isPromotionCollapsedOnScroll);
  isPromotionCollapsedRef.current = isPromotionCollapsedOnScroll;

  /** Avoid triggering synthetic scroll if the published height did not change. */
  const lastPublishedPromotionHeightRef = useRef<number | null>(null);

  /**
   * Header uses --top-promotion for --header-offset (fixed header padding).
   * If we only measured when `messages` changed, rotating slides (Swiper autoHeight)
   * changes the height but the header padding stayed with the old value -> gap.
   * ResizeObserver keeps them in sync; we also dispatch `scroll` so Header re-reads
   * the offset without requiring the user to move the page.
   */
  /**
   * useLayoutEffect (not useEffect): before the first paint, so the fixed header
   * reads --top-promotion in its own useLayoutEffect and does not keep padding 0
   * on F5/hydration (promo z-index 11 above header z-index 10).
   */
  useLayoutEffect(() => {
    const element = promotionRef.current;

    if (!element || element.id !== 'promotion-id-layout') {
      document.documentElement.style.setProperty('--top-promotion', '0px');
      return;
    }

    const notifyHeaderOffset = () => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('scroll'));
      });
    };

    const syncTopPromotionFromDom = () => {
      if (isPromotionCollapsedRef.current) {
        document.documentElement.style.setProperty('--top-promotion', '0px');
        if (lastPublishedPromotionHeightRef.current !== 0) {
          lastPublishedPromotionHeightRef.current = 0;
          notifyHeaderOffset();
        }
        return;
      }

      const h = Math.round(element.getBoundingClientRect().height);
      if (h > 0) {
        setMeasuredPromotionHeight(h);
        document.documentElement.style.setProperty('--top-promotion', `${h}px`);
        if (lastPublishedPromotionHeightRef.current !== h) {
          lastPublishedPromotionHeightRef.current = h;
          notifyHeaderOffset();
        }
      }
    };

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(syncTopPromotionFromDom);
    });

    ro.observe(element);
    syncTopPromotionFromDom();

    return () => {
      ro.disconnect();
      lastPublishedPromotionHeightRef.current = null;
      document.documentElement.style.setProperty('--top-promotion', '0px');
    };
  }, [location, actives.length, messages, isPromotionCollapsedOnScroll]);

  const isCheck = shouldHideNavigation(pathname as string);

  const handleDismiss = useCallback(() => {
    if (actives.length <= 1) {
      setState({ is_promotionbar_open: false });
      return;
    }

    const msg = actives[current.index];
    const baseIdx = baseActives.findIndex(m => m === msg);
    if (baseIdx >= 0) {
      setHiddenBaseIndices(prev => new Set(prev).add(baseIdx));
    }
  }, [actives, baseActives, current.index, setState]);

  const handleSwiperChange = useCallback((swiper: ISwiper) => {
    const index = swiper?.activeIndex ?? 0;
    const list = activesRef.current;
    setCurrent({
      dismissible: list[index]?.dismissible ?? false,
      index,
    });
  }, []);

  if (
    isCheck ||
    !state.is_promotionbar_open ||
    !actives.length ||
    (location === 'layout' &&
      activeSlice?.variation === 'withCenteredLogoAndLocatorFix')
  ) {
    return null;
  }

  const hasPromotion = actives.length > 0;
  const swiperKey = actives.map(m => m.message).join('|');

  const isFloating = !!activeSlice?.primary?.floating;

  return (
    <aside
      aria-hidden={isPromotionCollapsedOnScroll ? true : undefined}
      aria-label={`Promotional banner, ${actives.length} ${
        actives.length === 1 ? 'message' : 'messages'
      }`}
      className={classNames(style.promotion, className, {
        [style.promotion__sticky]: isServerSide && isFloating,
        [style.promotion__hidden]: isPromotionCollapsedOnScroll,
      })}
      id={`promotion-id-${location}`}
      ref={promotionRef}
      {...props}
    >
      <Alert
        className={classNames(style.promotion__text, style.alert)}
        floatingAction
        isOpen={state.is_promotionbar_open}
        showIcon={false}
        toggle={current.dismissible ? handleDismiss : undefined}
      >
        <Swiper
          allowTouchMove={hasPromotion}
          autoHeight={true}
          autoplay={{
            delay: delay ?? 6000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          className={style.promotion_swiper}
          effect={'fade'}
          fadeEffect={{ crossFade: true }}
          key={swiperKey}
          loop={actives.length > 1}
          modules={[Autoplay, EffectFade]}
          onSlideChange={handleSwiperChange}
          slidesPerView={1}
          spaceBetween={30}
        >
          {actives.map((message, index) => {
            const resolved = resolveLink(message?.internalCta, message?.cta, {
              externalNewTab: true,
              downloadMedia: false,
            });

            const opensNewTab =
              resolved.kind === 'external' || resolved.kind === 'media';

            const ariaLabel =
              opensNewTab && message?.message
                ? `${message.message} (opens in a new tab)`
                : message.message;

            const content =
              resolved.kind === 'empty' ? (
                <span>{message.message}</span>
              ) : (
                <PrismicNextLink
                  {...(resolved.props as any)}
                  aria-label={ariaLabel}
                >
                  {message.message}
                </PrismicNextLink>
              );

            return (
              <SwiperSlide className={style.promotion_swiper__item} key={index}>
                {content}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Alert>
    </aside>
  );
};

export default Promotion;
